// §4.8 — QUALITY ANALYTICS, /quality/analytics (spec 07 §2.12, §5; slice
// 07-E; D-216). Four panels over the one analytics code, each §5 role cell
// the SERVER's (D-040): the FPY trend (an inline-SVG line over `group_by=day`
// — the calibration drift chart's idiom) with the dimension league table
// beside it (`product|work_center|shift`; `step` deferred, D-216 pt 3), the
// failure-cause Pareto with its count/value toggle (§7's money pair arrives
// only for the value set — the toggle renders for everyone, the money for
// some), the per-shift scoreboard for one date, and the supplier
// receiving-failure table (qc/procurement/ga — the panel hides for a caller
// whose GET would 403: D-118's fixable-vs-permission split, and this is the
// permission half).
//
// THE STALENESS LINE IS THE PROJECTION'S OWN STAMP (`refreshed_at` off the
// fpy read — §4.8's "as of 14:00"); the Refresh button renders for
// group_admin alone (D-216-B) and forces `qc_fpy_refresh(0)`.

import type { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { RefLabel } from "../../../../components/ref-label";
import type { RefKind } from "../../../../lib/ref-names";
import { facilityTodayIso } from "../../../../lib/business-dates";
import { formatBusinessDate } from "../../../../lib/datetime";
import { useTenantTimezone } from "../../../../lib/role-codes";
import { formatRatioAsPercent } from "../../../../lib/quantity";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Select } from "../../../../components/select";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useFacilities } from "../../inventory/hooks/inventory-queries";
import { formatMinor } from "../../production/lib/format";
import { useWorkCenters } from "../../production/hooks/production-queries";
import { isQualityReadRefused, useQualityPermissions } from "../hooks/permissions";
import { localeText } from "../run/inspection-run";
import {
  useQualityFailureCauses,
  useQualityFilterProducts,
  useQualityFpy,
  useQualityPareto,
  useQualityScoreboard,
  useQualitySuppliers,
  useRefreshAnalytics,
  type QualityParetoCause,
  type QualityShiftScoreboardRow,
  type QualitySupplierReceivingRow,
} from "../hooks/quality-queries";

type Dim = "product" | "work_center" | "shift";

/** The names read's kind for a league key, by dimension. A shift has no kind
 * there, so its key stays the short-id chip. */
export function leagueKeyKind(dim: Dim): RefKind | undefined {
  return dim === "product" || dim === "work_center" ? dim : undefined;
}

/**
 * Rows per page in each of the four tables (rev-7 client pager, D-426). Every
 * table holds every row its read returns, so without a pager the screen grew
 * with the tenant's data: 3,957 px at 1440x900 on staging, over the four-screen
 * cap (D-451). Four full pages of ten keep it under three screens.
 */
export const ANALYTICS_PAGE_SIZE = 10;

/** The calendar date `days` before `dateIso` (pure calendar arithmetic).
 * The default window is anchored on the TENANT's today (D-089), never the
 * UTC date: from 00:00 to 05:00 in Almaty the UTC "today" is yesterday, so
 * the window left out every inspection of the running day (D-600). */
function isoDaysBefore(dateIso: string, days: number): string {
  const at = new Date(`${dateIso}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() - days);
  return at.toISOString().slice(0, 10);
}

export function QualityAnalyticsScreen() {
  const { t, i18n } = useTranslation();
  const perms = useQualityPermissions();
  const refused = isQualityReadRefused(perms, "quality.analytics.read");
  const isGroupAdmin = perms.has("quality.vertical_pack.seed");
  // The §5 cells the SCREEN mirrors (the server enforces the same ones — a
  // panel never renders whose GET would 403). The permission feed carries
  // CODES, not roles, so each role cell is inferred through its
  // DISTINGUISHING codes: procurement is the one analytics holder with
  // `quality.hold.disposition` but not `quality.resolution.dispose` (qc holds
  // both); floor_worker the one without `quality.hold.read` at all. The
  // web-permission-mirror gate keeps these tables aligned with the contract,
  // so the inference cannot silently drift.
  const isProcurementShaped =
    perms.has("quality.hold.disposition") && !perms.has("quality.resolution.dispose");
  const canSeeFpy = perms.has("quality.hold.read") && !isProcurementShaped;
  // F6: procurement's §5 cell is the supplier panel ALONE — the scoreboard
  // panel hides for a procurement-shaped session exactly as the server now
  // refuses it.
  const canSeeScoreboard = !isProcurementShaped;
  const canSeeSuppliers =
    isGroupAdmin || isProcurementShaped || perms.has("quality.resolution.dispose");

  const [grain, setGrain] = useState<"daily" | "weekly">("daily");
  const today = facilityTodayIso(Date.now(), useTenantTimezone());
  // A date the reader picked wins; until then each default follows the
  // tenant's today, which is only known once the membership feed lands.
  const [fromPick, setFrom] = useState<string | undefined>(undefined);
  const [toPick, setTo] = useState<string | undefined>(undefined);
  const from = fromPick ?? isoDaysBefore(today, 30);
  const to = toPick ?? today;
  const [dim, setDim] = useState<Dim>("work_center");
  const [paretoBy, setParetoBy] = useState<"count" | "value">("count");
  const [facilityId, setFacilityId] = useState("");
  const [scorePick, setScoreDate] = useState<string | undefined>(undefined);
  const scoreDate = scorePick ?? isoDaysBefore(today, 1);

  const facilities = useFacilities();
  const trend = useQualityFpy(
    { from, to, groupBy: "day", ...(facilityId === "" ? {} : { facilityId }) },
    !refused && canSeeFpy,
  );
  const league = useQualityFpy(
    { from, to, groupBy: dim, ...(facilityId === "" ? {} : { facilityId }) },
    !refused && canSeeFpy,
  );
  const pareto = useQualityPareto(
    { from, to, by: paretoBy, ...(facilityId === "" ? {} : { facilityId }) },
    !refused && canSeeFpy,
  );
  const scoreboard = useQualityScoreboard(
    {
      date: scoreDate,
      facilityId: facilityId === "" ? (facilities.data?.items[0]?.id ?? "") : facilityId,
    },
    !refused && canSeeScoreboard,
  );
  const suppliers = useQualitySuppliers({ from, to }, !refused && canSeeSuppliers);
  const refresh = useRefreshAnalytics();
  // The four tables share one pager vocabulary. Each table's resetKey is its
  // own filter, so a reader on page 3 who changes it starts at page 1.
  const pagerWords = {
    range: (first: number, last: number, total: number) =>
      t("quality.common.range", { from: first, to: last, total }),
    prevLabel: t("quality.common.prevPage"),
    nextLabel: t("quality.common.nextPage"),
  };
  const facilityKey = facilityId === "" ? "all" : facilityId;
  // F10: league/Pareto keys are ids — resolve names from the area's existing
  // reference queries (products; work centres per facility). The SHIFT
  // dimension has no client-side reference read yet; its keys render as ids
  // until the §10.7 drill-through successor lands the shifts read.
  const products = useQualityFilterProducts(!refused && canSeeFpy && dim === "product");
  const workCenters = useWorkCenters(facilityId === "" ? undefined : facilityId, "active");
  const keyKind = leagueKeyKind(dim);
  // Cause names in the reader's language, for the Pareto and the supplier
  // table. Both reads carry the cause CODE; the Pareto's own `name` is the
  // fallback, and a code shows only for a cause the list no longer holds.
  const causes = useQualityFailureCauses(!refused && (canSeeFpy || canSeeSuppliers));
  const causeName = useMemo(() => {
    const map = new Map<string, string>();
    for (const cause of causes.data ?? [])
      map.set(cause.code, localeText(cause.name_i18n, i18n.language, cause.code));
    return map;
  }, [causes.data, i18n.language]);
  const keyName = useMemo(() => {
    const map = new Map<string, string>();
    for (const prod of products.data ?? []) map.set(prod.id, prod.sku);
    for (const wc of workCenters.data?.items ?? []) map.set(wc.id, wc.code);
    return map;
  }, [products.data, workCenters.data]);

  // F16: the weekly grain is a CLIENT-SIDE aggregation of the day series —
  // one grain on the wire, two on the glass (§4.8 as amended).
  const trendBuckets = useMemo(() => {
    const days = (trend.data?.buckets ?? []).filter(
      (b): b is typeof b & { key: string } => b.key !== null,
    );
    if (grain === "daily") return days;
    const weeks = new Map<string, { key: string; total: number; passed_first_time: number }>();
    for (const b of days) {
      const d = new Date(`${b.key}T00:00:00Z`);
      // ISO week key: the Monday of the bucket's week.
      const dow = (d.getUTCDay() + 6) % 7;
      const monday = new Date(d.getTime() - dow * 86_400_000).toISOString().slice(0, 10);
      const w = weeks.get(monday) ?? { key: monday, total: 0, passed_first_time: 0 };
      w.total += b.total;
      w.passed_first_time += b.passed_first_time;
      weeks.set(monday, w);
    }
    return [...weeks.values()].map((w) => ({
      key: w.key,
      total: w.total,
      passed_first_time: w.passed_first_time,
      fpy: w.total === 0 ? null : (w.passed_first_time / w.total).toFixed(4),
    }));
  }, [trend.data, grain]);

  // The days that carry a yield; a day with no finished inspection has none.
  const trendPoints = trendBuckets
    .filter((b): b is typeof b & { fpy: string } => b.fpy !== null)
    .map((b) => ({ day: b.key, fpy: Number(b.fpy) }));

  const paretoMax = Math.max(
    1,
    ...(pareto.data ?? []).map((c) =>
      paretoBy === "value" ? (c.value_at_risk_minor ?? 0) : c.count,
    ),
  );

  const leagueColumns: DataTableColumn<{
    key: string | null;
    total: number;
    passed_first_time: number;
    fpy: string | null;
  }>[] = [
    {
      key: "key",
      header: t("quality.analytics.colKey"),
      id: true,
      // F10: resolved names where the area has a reference read; F9: the
      // NULL key is translated HERE, never a server string.
      render: (r) =>
        r.key === null ? (
          t("quality.analytics.unstampedKey")
        ) : (
          // Rev-7: an id the screen cannot name is a short chip, never a
          // full uuid; the batched names read (UP6) fills in the rest.
          <RefLabel
            value={keyName.get(r.key)}
            id={r.key}
            {...(keyKind === undefined ? {} : { kind: keyKind })}
          />
        ),
    },
    {
      key: "total",
      header: t("quality.analytics.colTotal"),
      numeric: true,
      render: (r) => String(r.total),
    },
    {
      key: "passed",
      header: t("quality.analytics.colPassedFirstTime"),
      numeric: true,
      render: (r) => String(r.passed_first_time),
    },
    {
      // D-123 r8 (review F13): this module's yield is NAMED — "first-pass
      // yield (inspections)" — never a bare "FPY" that module 04's quantity
      // yield could be mistaken for.
      key: "fpy",
      header: t("quality.analytics.fpyLabel"),
      numeric: true,
      render: (r) =>
        r.fpy === null ? "—" : formatRatioAsPercent(r.fpy, { locale: i18n.language }),
    },
  ];

  const paretoColumns: DataTableColumn<QualityParetoCause>[] = [
    {
      key: "code",
      header: t("quality.analytics.colCause"),
      id: true,
      render: (r) => causeName.get(r.code) ?? (r.name.length > 0 ? r.name : r.code),
    },
    {
      key: "count",
      header: t("quality.analytics.colCount"),
      numeric: true,
      render: (r) => String(r.count),
    },
    {
      key: "value",
      header: t("quality.analytics.colValue"),
      numeric: true,
      // §7: absence, not a dash-for-zero — a redacted caller sees NO money
      // column content anywhere on the row.
      render: (r) =>
        r.value_at_risk_minor !== undefined && r.currency !== undefined ? (
          <span data-testid={`pareto-value-${r.code}`}>
            {formatMinor(r.value_at_risk_minor, r.currency, i18n.language)}
          </span>
        ) : (
          <span data-testid={`pareto-novalue-${r.code}`}>—</span>
        ),
    },
    {
      key: "bar",
      header: "",
      render: (r) => {
        const magnitude = paretoBy === "value" ? (r.value_at_risk_minor ?? 0) : r.count;
        return (
          <svg
            className="quality-pareto-bar"
            viewBox="0 0 100 8"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <rect x="0" y="0" height="8" width={(magnitude / paretoMax) * 100} rx="2" />
          </svg>
        );
      },
    },
  ];

  const scoreboardColumns: DataTableColumn<QualityShiftScoreboardRow>[] = [
    {
      key: "shift",
      header: t("quality.analytics.colShift"),
      id: true,
      // F9: the unattributed row crosses the wire as NULL and is translated
      // here.
      render: (r) => r.shift_label ?? t("quality.analytics.unattributed"),
    },
    {
      key: "inspections",
      header: t("quality.analytics.colInspections"),
      numeric: true,
      render: (r) => String(r.inspections),
    },
    {
      key: "produced",
      header: t("quality.analytics.colDefectsProduced"),
      numeric: true,
      render: (r) => String(r.defects_produced),
    },
    {
      key: "caught",
      header: t("quality.analytics.colDefectsCaught"),
      numeric: true,
      render: (r) => String(r.defects_caught),
    },
    {
      key: "handoffs",
      header: t("quality.analytics.colHandoffs"),
      numeric: true,
      render: (r) => String(r.handoff_incidents),
    },
    {
      key: "fpy",
      header: t("quality.analytics.fpyLabel"),
      numeric: true,
      render: (r) =>
        r.fpy === null ? "—" : formatRatioAsPercent(r.fpy, { locale: i18n.language }),
    },
  ];

  const supplierColumns: DataTableColumn<QualitySupplierReceivingRow>[] = [
    {
      key: "supplier",
      header: t("quality.analytics.colSupplier"),
      id: true,
      // No module names a supplier yet (module 10 is unbuilt), so the id is
      // the short chip with the full id on its title, never 36 characters.
      render: (r) => <RefLabel id={r.supplier_id} />,
    },
    {
      key: "receipts",
      header: t("quality.analytics.colReceipts"),
      numeric: true,
      render: (r) => String(r.receipts),
    },
    {
      key: "failures",
      header: t("quality.analytics.colFailures"),
      numeric: true,
      render: (r) => String(r.failures),
    },
    {
      key: "rate",
      header: t("quality.analytics.colFailureRate"),
      numeric: true,
      render: (r) => formatRatioAsPercent(r.failure_rate, { locale: i18n.language }),
    },
    {
      key: "cause",
      header: t("quality.analytics.colTopCause"),
      render: (r) => (r.top_cause === null ? "—" : (causeName.get(r.top_cause) ?? r.top_cause)),
    },
  ];

  return (
    <section className="quality-analytics" data-testid="quality-analytics">
      <PageHeader
        title={t("quality.analytics.title")}
        actions={
          <>
            {isGroupAdmin ? (
              <Button
                size="sm"
                variant="secondary"
                data-testid="analytics-refresh"
                busy={refresh.isPending}
                onClick={() => {
                  refresh.mutate();
                }}
              >
                {t("quality.analytics.refresh")}
              </Button>
            ) : null}
          </>
        }
      />

      {/* §4.8's staleness indicator — the projection's own stamp, rendered in
          the viewer's locale and zone (review F11: a raw UTC slice told an
          Almaty qc a time five hours wrong). */}
      {trend.data !== undefined ? (
        <p className="quality-note" data-testid="analytics-staleness">
          {t("quality.analytics.asOf", {
            at: new Intl.DateTimeFormat(i18n.language, {
              dateStyle: "medium",
              timeStyle: "short",
            }).format(new Date(trend.data.refreshed_at)),
          })}
        </p>
      ) : null}

      <div className="filter-bar" data-testid="analytics-filters">
        <Field label={t("quality.analytics.filterFrom")}>
          <input
            type="date"
            className="ds-input"
            value={from}
            data-testid="analytics-from"
            onChange={(e) => {
              setFrom(e.target.value);
            }}
          />
        </Field>
        <Field label={t("quality.analytics.filterTo")}>
          <input
            type="date"
            className="ds-input"
            value={to}
            data-testid="analytics-to"
            onChange={(e) => {
              setTo(e.target.value);
            }}
          />
        </Field>
        <Field label={t("quality.analytics.filterFacility")}>
          <Select
            value={facilityId}
            data-testid="analytics-facility"
            onChange={(e) => {
              setFacilityId(e.target.value);
            }}
          >
            <option value="">{t("quality.analytics.allFacilities")}</option>
            {(facilities.data?.items ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.code}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {refused ? (
        <ForbiddenState title={t("quality.access.title")} body={t("quality.access.analytics")} />
      ) : null}

      {!refused && trend.fetchStatus === "paused" && trend.data === undefined ? (
        <OfflineState title={t("quality.offline.title")} body={t("quality.offline.body")} />
      ) : null}

      {!refused && trend.error !== null && !(trend.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("quality.analytics.error")}
          retryLabel={t("quality.common.retry")}
          requestIdLabel={t("quality.common.requestId")}
          requestId={requestIdOf(trend.error)}
          onRetry={() => {
            void trend.refetch();
          }}
        />
      ) : null}

      {!refused && trend.isPending && trend.fetchStatus !== "paused" ? (
        <TableSkeleton rows={4} cols={4} label={t("quality.common.loading")} />
      ) : null}

      {!refused && trend.data !== undefined ? (
        <>
          {/* -- the yield trend (inline SVG — the drift-chart idiom). The
                weekly grain aggregates the day series CLIENT-SIDE (§4.8 as
                amended, review F16). */}
          <div className="filter-bar">
            <h2 className="quality-panel-head">{t("quality.analytics.trendTitle")}</h2>
            <Field label={t("quality.analytics.filterGrain")}>
              <Select
                value={grain}
                data-testid="analytics-grain"
                onChange={(e) => {
                  setGrain(e.target.value as "daily" | "weekly");
                }}
              >
                <option value="daily">{t("quality.analytics.grainDaily")}</option>
                <option value="weekly">{t("quality.analytics.grainWeekly")}</option>
              </Select>
            </Field>
          </div>
          {trendPoints.length === 0 ? (
            /* Rev-7: a window whose days carry no yield (no inspection
               finished) used to draw an empty SVG, a blank white block. */
            <EmptyState
              title={t("quality.analytics.emptyTitle")}
              body={t("quality.analytics.emptyBody")}
              data-testid="analytics-empty"
            />
          ) : (
            <FpyTrend
              label={t("quality.analytics.fpyLabel")}
              latest={fpyLatestText(t, trendPoints, i18n.language)}
              buckets={trendPoints}
            />
          )}

          {/* -- dimension league table ------------------------------------- */}
          <div className="filter-bar">
            <h2 className="quality-panel-head">{t("quality.analytics.leagueTitle")}</h2>
            <Field label={t("quality.analytics.filterDimension")}>
              <Select
                value={dim}
                data-testid="analytics-dimension"
                onChange={(e) => {
                  setDim(e.target.value as Dim);
                }}
              >
                <option value="product">{t("quality.analytics.dimProduct")}</option>
                <option value="work_center">{t("quality.analytics.dimWorkCenter")}</option>
                <option value="shift">{t("quality.analytics.dimShift")}</option>
              </Select>
            </Field>
          </div>
          {league.data !== undefined ? (
            <DataTable
              columns={leagueColumns}
              rows={league.data.buckets}
              rowKey={(r) => r.key ?? "unstamped"}
              empty={null}
              caption={t("quality.analytics.leagueTitle")}
              clientPager={{
                ...pagerWords,
                pageSize: ANALYTICS_PAGE_SIZE,
                resetKey: [dim, from, to, facilityKey].join("|"),
              }}
              data-testid="analytics-league-table"
            />
          ) : null}

          {/* -- failure Pareto --------------------------------------------- */}
          <div className="filter-bar">
            <h2 className="quality-panel-head">{t("quality.analytics.paretoTitle")}</h2>
            <Field label={t("quality.analytics.filterBy")}>
              <Select
                value={paretoBy}
                data-testid="analytics-pareto-by"
                onChange={(e) => {
                  setParetoBy(e.target.value as "count" | "value");
                }}
              >
                <option value="count">{t("quality.analytics.byCount")}</option>
                <option value="value">{t("quality.analytics.byValue")}</option>
              </Select>
            </Field>
          </div>
          {pareto.data !== undefined ? (
            <DataTable
              columns={paretoColumns}
              rows={pareto.data}
              rowKey={(r) => r.code}
              empty={null}
              caption={t("quality.analytics.paretoTitle")}
              clientPager={{
                ...pagerWords,
                pageSize: ANALYTICS_PAGE_SIZE,
                resetKey: [paretoBy, from, to, facilityKey].join("|"),
              }}
              data-testid="analytics-pareto-table"
            />
          ) : null}

          {/* -- shift scoreboard (hidden for procurement-shaped sessions —
                their §5 cell is the supplier panel alone, review F6) -------- */}
          {canSeeScoreboard ? (
            <div className="filter-bar">
              <h2 className="quality-panel-head">{t("quality.analytics.scoreboardTitle")}</h2>
              <Field label={t("quality.analytics.filterDate")}>
                <input
                  type="date"
                  className="ds-input"
                  value={scoreDate}
                  data-testid="analytics-score-date"
                  onChange={(e) => {
                    setScoreDate(e.target.value);
                  }}
                />
              </Field>
            </div>
          ) : null}
          {canSeeScoreboard && scoreboard.data !== undefined ? (
            <DataTable
              columns={scoreboardColumns}
              rows={scoreboard.data}
              rowKey={(r) => r.shift_instance_id ?? "unattributed"}
              empty={
                <EmptyState
                  title={t("quality.analytics.scoreEmptyTitle")}
                  body={t("quality.analytics.scoreEmptyBody")}
                />
              }
              caption={t("quality.analytics.scoreboardTitle")}
              clientPager={{
                ...pagerWords,
                pageSize: ANALYTICS_PAGE_SIZE,
                resetKey: [scoreDate, facilityKey].join("|"),
              }}
              data-testid="analytics-scoreboard-table"
            />
          ) : null}

          {/* -- supplier receiving (qc/procurement/ga — the §5 cell) -------- */}
          {canSeeSuppliers && suppliers.data !== undefined ? (
            <>
              <h2 className="quality-panel-head">{t("quality.analytics.supplierTitle")}</h2>
              <DataTable
                columns={supplierColumns}
                rows={suppliers.data}
                rowKey={(r) => r.supplier_id}
                empty={
                  <EmptyState
                    title={t("quality.analytics.supplierEmptyTitle")}
                    body={t("quality.analytics.supplierEmptyBody")}
                  />
                }
                caption={t("quality.analytics.supplierTitle")}
                clientPager={{
                  ...pagerWords,
                  pageSize: ANALYTICS_PAGE_SIZE,
                  resetKey: [from, to].join("|"),
                }}
                data-testid="analytics-supplier-table"
              />
            </>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

/** The yield trend line — inline SVG, the calibration drift chart's
 * register; the accessible name is D-123 r8's labelled yield (review F13).
 * Rev-7: the line is stroked (it had no stroke rule, so it drew nothing), each
 * day is a dot so a one-day window still shows, and the latest value is
 * written under the chart. */
export function fpyLatestText(
  t: TFunction,
  buckets: readonly { day: string; fpy: number }[],
  locale: string,
): string {
  const last = buckets[buckets.length - 1];
  return t("quality.analytics.trendLatest", {
    value: formatRatioAsPercent(last?.fpy, { locale }),
    // The bucket's day is a business date; it reads as one, never as ISO.
    day: formatBusinessDate(last?.day ?? "", locale),
  });
}

export function FpyTrend(props: {
  label: string;
  latest: string;
  buckets: { day: string; fpy: number }[];
}) {
  const W = 600;
  const H = 120;
  const PAD = 8;
  const n = props.buckets.length;
  const x = (i: number) => (n === 1 ? W / 2 : PAD + (i * (W - 2 * PAD)) / (n - 1));
  const y = (fpy: number) => H - PAD - fpy * (H - 2 * PAD);
  const path = props.buckets
    .map((b, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(b.fpy).toFixed(1)}`)
    .join(" ");
  return (
    <figure className="quality-fpy-figure">
      <svg
        className="quality-fpy-trend"
        viewBox={`0 0 ${String(W)} ${String(H)}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={props.label}
        data-testid="analytics-fpy-trend"
      >
        {[0, 0.5, 1].map((v) => (
          <line key={v} className="quality-fpy-grid" x1={0} x2={W} y1={y(v)} y2={y(v)} />
        ))}
        <path className="quality-fpy-line" d={path} fill="none" />
        {/* A dot is a zero-length line with a round cap and a non-scaling
            stroke: the svg stretches (preserveAspectRatio="none"), which
            squashed a <circle> into an ellipse. */}
        {props.buckets.map((b, i) => (
          <line
            key={b.day}
            className="quality-fpy-dot"
            x1={x(i)}
            x2={x(i)}
            y1={y(b.fpy)}
            y2={y(b.fpy)}
          />
        ))}
      </svg>
      <figcaption className="quality-fpy-latest" data-testid="analytics-fpy-latest">
        {props.latest}
      </figcaption>
    </figure>
  );
}
